import React from 'react';
import {Link, NavLink, useLocation, useNavigate} from 'react-router-dom';

import EvalOverviewMap from 'src/components/eval/EvalOverviewMap';
import ForecastSparkline from 'src/components/eval/ForecastSparkline';
import SeasonalIndicatorsMini from 'src/components/eval/SeasonalIndicatorsMini';
import TimeWindowSelector from 'src/components/eval/TimeWindowSelector';
import LoadingSpinner from 'src/components/LoadingSpinner';
import PageContainer from 'src/components/PageContainer';
import {useEvalSitesOverviewGlobal} from 'src/hooks/eval/useEvalOverviewGlobal';
import useAuth from 'src/hooks/useAuth';
import {
  EvalOverviewSite,
  EvalOverviewSiteModelStats,
  EvalProduct,
  EvalSite,
  EvalSitesOverview,
  TimeWindow,
} from 'src/lib/api/eval';
import * as tw from 'src/lib/tailwindClasses';

type SortBy =
  | 'name'
  | 'nrmse-asc'
  | 'nrmse-desc'
  | 'kge-asc'
  | 'kge-desc'
  | 'crps-asc'
  | 'crps-desc'
  | 'mae-asc'
  | 'mae-desc'
  | 'percentBias-asc'
  | 'percentBias-desc';

// Relative comparison: 'good' = HF is best vs baselines, 'bad' = a baseline beats HF
function metricStatus(
  metric: string,
  hfValue: number | null | undefined,
  models: Record<string, EvalOverviewSiteModelStats> | undefined,
  product: EvalProduct
): 'good' | 'bad' | null {
  if (hfValue == null || !Number.isFinite(hfValue) || !models) return null;

  // Short-term: compare vs persistence + alt forecasts (not LTA)
  // Seasonal: compare vs LTA only
  const comparisonKeys =
    product === 'seasonal'
      ? ['lta']
      : Object.keys(models).filter((m) => m !== 'hydroforecast' && m !== 'lta');

  const comparisonValues: number[] = comparisonKeys
    .map((k) => models[k]?.[metric as keyof EvalOverviewSiteModelStats] as number | undefined)
    .filter((v): v is number => v != null && Number.isFinite(v));

  if (comparisonValues.length === 0) return null;

  // Is HF the best?
  const isBest =
    metric === 'percentBias'
      ? comparisonValues.every((cv) => Math.abs(hfValue) <= Math.abs(cv))
      : metric === 'kge'
        ? comparisonValues.every((cv) => hfValue >= cv)
        : comparisonValues.every((cv) => hfValue <= cv); // nrmse, mae, crps

  return isBest ? 'good' : 'bad';
}

const STATUS_CLASSES: Record<string, string> = {
  good: 'bg-green-100 text-green-800',
  bad: 'bg-red-100 text-red-800',
};

const STATUS_COLORS: Record<string, string> = {
  good: '#16a34a',
  bad: '#dc2626',
};

function metricColor(
  metric: string,
  hfValue: number | null | undefined,
  models: Record<string, EvalOverviewSiteModelStats> | undefined,
  product: EvalProduct
): string {
  const status = metricStatus(metric, hfValue, models, product);
  return status ? STATUS_COLORS[status] : '#888';
}

type EvalOverviewSiteWithMetric = EvalOverviewSite & {
  _metricValue: number | null;
  _metricColor: string;
};

function getHfMetric(
  site: EvalOverviewSite,
  metric: keyof EvalOverviewSiteModelStats
): number | null {
  const v = site.models?.hydroforecast?.[metric] as number | undefined;
  if (v === null || v === undefined || !Number.isFinite(v)) return null;
  return v;
}

function getSiteDetailUrl(
  site: EvalSite & {organizationId?: string},
  product?: EvalProduct
): string {
  const orgId = site.organizationId;
  const horizon = product === 'seasonal' ? '?horizon=seasonal' : '';
  if (orgId) {
    return `/${orgId}/eval/${site.siteId}${horizon}`;
  }
  return `/eval/${site.siteId}${horizon}`;
}

const VALID_WINDOWS = new Set<string>(['10d', '30d', '90d', '180d', '365d', 'all']);
const VALID_SORTS = new Set<string>([
  'name',
  'nrmse-asc',
  'nrmse-desc',
  'kge-asc',
  'kge-desc',
  'crps-asc',
  'crps-desc',
  'mae-asc',
  'mae-desc',
  'percentBias-asc',
  'percentBias-desc',
]);

const PRODUCT_TABS: Array<{key: EvalProduct; label: string}> = [
  {key: 'short-term', label: 'Short-Term (10d)'},
  {key: 'seasonal', label: 'Seasonal (365d)'},
];

export default function EvalGlobalOverview({product = 'short-term'}: {product?: EvalProduct}) {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  const isSeasonal = product === 'seasonal';
  const defaultWindow: TimeWindow = isSeasonal ? '90d' : '10d';

  // Read filter state from URL search params
  const searchParams = new URLSearchParams(location.search);

  const windowParam = searchParams.get('window');
  const window: TimeWindow =
    windowParam && VALID_WINDOWS.has(windowParam) ? (windowParam as TimeWindow) : defaultWindow;

  const sortParam = searchParams.get('sort');
  const sortBy: SortBy =
    sortParam && VALID_SORTS.has(sortParam) ? (sortParam as SortBy) : 'nrmse-desc';

  const filterOrg = searchParams.get('org') || null;
  const groupByCustomer = searchParams.get('group') !== 'false';

  const [hoveredSiteId, setHoveredSiteId] = React.useState<string | null>(null);

  // Helper to update a single URL search param (replaces history entry)
  const setParam = React.useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(location.search);
      if (value == null || value === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      const qs = next.toString();
      navigate({pathname: location.pathname, search: qs ? `?${qs}` : ''}, {replace: true});
    },
    [navigate, location.pathname, location.search]
  );

  const setWindow = React.useCallback(
    (w: TimeWindow) => setParam('window', w === defaultWindow ? null : w),
    [setParam, defaultWindow]
  );
  const setSortBy = React.useCallback(
    (s: SortBy) => setParam('sort', s === 'nrmse-desc' ? null : s),
    [setParam]
  );
  const setFilterOrg = React.useCallback((org: string | null) => setParam('org', org), [setParam]);
  const setGroupByCustomer = React.useCallback(
    (g: boolean) => setParam('group', g ? null : 'false'),
    [setParam]
  );

  const {overview, isLoaded, isFetching, isError, error, refetch} = useEvalSitesOverviewGlobal(
    window,
    product
  );

  const [lastGoodOverview, setLastGoodOverview] = React.useState<EvalSitesOverview | null>(null);
  React.useEffect(() => {
    if (overview) setLastGoodOverview(overview);
  }, [overview]);

  const effectiveOverview = overview ?? lastGoodOverview;
  const sitesRaw = React.useMemo<EvalOverviewSite[]>(
    () => effectiveOverview?.sites ?? [],
    [effectiveOverview]
  );

  // Build org ID → name map from the already-loaded profile organization tree
  const orgNamesById = React.useMemo(() => {
    const profileOrgs = auth.status === 'logged in' ? auth.profileOrganizations : [];
    const map: Record<string, string> = {};
    for (const profileOrg of profileOrgs) {
      map[profileOrg.id] = profileOrg.name;
      for (const child of profileOrg.childOrganizations ?? []) {
        map[child.id] = child.name;
      }
    }
    return map;
  }, [auth]);

  // Available organizations for the filter dropdown
  const orgOptions = React.useMemo(() => {
    const orgIds = new Set<string>();
    for (const s of sitesRaw) {
      if (s.organizationId) orgIds.add(s.organizationId);
    }
    return Array.from(orgIds)
      .map((id) => ({id, name: orgNamesById[id] ?? id}))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [sitesRaw, orgNamesById]);

  // Reset filter if the selected org no longer exists in the loaded data
  React.useEffect(() => {
    if (filterOrg && orgOptions.length > 0 && !orgOptions.some((o) => o.id === filterOrg)) {
      setFilterOrg(null);
    }
  }, [filterOrg, orgOptions, setFilterOrg]);

  const filteredSitesRaw = React.useMemo(
    () => (filterOrg ? sitesRaw.filter((s) => s.organizationId === filterOrg) : sitesRaw),
    [sitesRaw, filterOrg]
  );

  const sites = React.useMemo(() => {
    const activeMetric = (
      sortBy === 'name' ? 'nrmse' : sortBy.replace(/-(?:asc|desc)$/, '')
    ) as keyof EvalOverviewSiteModelStats;
    const withMetric: EvalOverviewSiteWithMetric[] = filteredSitesRaw.map((s) => {
      const value = getHfMetric(s, activeMetric);
      return {
        ...s,
        _metricValue: value,
        _metricColor: metricColor(activeMetric as string, value, s.models, product),
      };
    });

    const sorted = withMetric.slice().sort((a, b) => {
      if (sortBy === 'name') {
        return (a.displayName || a.siteId || '').localeCompare(b.displayName || b.siteId || '');
      }

      const metric = sortBy.replace(/-(?:asc|desc)$/, '') as keyof EvalOverviewSiteModelStats;
      const va = getHfMetric(a, metric);
      const vb = getHfMetric(b, metric);

      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;

      const isDesc = sortBy.endsWith('-desc');
      // Bias: "best" = closest to 0, "worst" = farthest from 0
      if (metric === 'percentBias') {
        const absA = Math.abs(va);
        const absB = Math.abs(vb);
        return isDesc ? absB - absA : absA - absB;
      }
      return isDesc ? vb - va : va - vb;
    });

    return sorted;
  }, [filteredSitesRaw, sortBy, product]);

  const sections = React.useMemo(() => {
    if (!groupByCustomer) {
      // Flat list — no grouping, no section headers
      return [{key: '_all', title: '', sites}];
    }

    // Group by organizationId
    const byOrg = new Map<string, EvalOverviewSiteWithMetric[]>();
    const ungrouped: EvalOverviewSiteWithMetric[] = [];
    for (const s of sites) {
      const org = s.organizationId;
      if (!org) {
        ungrouped.push(s);
        continue;
      }
      let list = byOrg.get(org);
      if (!list) {
        list = [];
        byOrg.set(org, list);
      }
      list.push(s);
    }

    const result = Array.from(byOrg.entries())
      .sort(([a], [b]) => {
        const nameA = orgNamesById[a] ?? a;
        const nameB = orgNamesById[b] ?? b;
        return nameA.localeCompare(nameB);
      })
      .map(([org, orgSites]) => ({key: org, title: orgNamesById[org] ?? org, sites: orgSites}));

    if (ungrouped.length) {
      result.push({key: 'ungrouped', title: 'Other sites', sites: ungrouped});
    }

    return result;
  }, [groupByCustomer, sites, orgNamesById]);

  return (
    <PageContainer>
      {isError && !effectiveOverview && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800 mb-4">
          <div className="font-medium">Couldn't load evaluation overview.</div>
          <div className="mt-1 text-red-700">
            {error instanceof Error ? error.message : 'Please try again.'}
          </div>
          <button type="button" className={`mt-3 ${tw.buttonSecondary}`} onClick={() => refetch()}>
            Retry
          </button>
        </div>
      )}

      {isError && !!effectiveOverview && (
        <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900 mb-4 flex items-center justify-between gap-4">
          <div>Couldn't refresh the overview. Showing the last loaded data.</div>
          <button type="button" className={tw.buttonSecondary} onClick={() => refetch()}>
            Retry
          </button>
        </div>
      )}

      {/* Two-column layout: sticky map left, controls + list right */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Left: sticky full-height map */}
        <div className="lg:w-1/2 lg:sticky lg:top-4 lg:h-[calc(100vh-10rem)] relative">
          <EvalOverviewMap
            sites={sites}
            highlightedSiteId={hoveredSiteId}
            onSiteClick={(siteId) => {
              const site = sites.find((s) => s.siteId === siteId);
              if (site && !site.skipReason) {
                navigate(getSiteDetailUrl(site, product));
              }
            }}
          />
          {!isLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/70">
              <LoadingSpinner />
            </div>
          )}
        </div>

        {/* Right: controls + scrollable site list */}
        <div className="lg:w-1/2 min-w-0">
          {/* Product tabs */}
          <div className="flex gap-1 mb-4 border-b border-gray-200">
            {PRODUCT_TABS.map((tab) => (
              <NavLink
                key={tab.key}
                to={`/eval/${tab.key}`}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                  product === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </NavLink>
            ))}
          </div>

          {/* Controls */}
          <div className="flex flex-col gap-3 mb-4">
            <div className="flex flex-wrap items-center gap-4">
              <TimeWindowSelector value={window} onChange={setWindow} />
              <select
                className={`${tw.select} w-44`}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortBy)}
                disabled={!effectiveOverview && !isLoaded}
              >
                <option value="name">Name (A-Z)</option>
                <option value="nrmse-asc">Best nRMSE</option>
                <option value="nrmse-desc">Worst nRMSE</option>
                <option value="kge-asc">Worst KGE</option>
                <option value="kge-desc">Best KGE</option>
                <option value="crps-asc">Best CRPS</option>
                <option value="crps-desc">Worst CRPS</option>
                <option value="mae-asc">Best MAE</option>
                <option value="mae-desc">Worst MAE</option>
                <option value="percentBias-asc">Best %Bias</option>
                <option value="percentBias-desc">Worst %Bias</option>
              </select>
              {orgOptions.length > 1 && (
                <select
                  className={`${tw.select} w-48`}
                  value={filterOrg ?? ''}
                  onChange={(e) => setFilterOrg(e.target.value || null)}
                >
                  <option value="">All organizations</option>
                  {orgOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              )}
              {!filterOrg && (
                <label className="text-sm text-gray-700 flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={groupByCustomer}
                    onChange={(e) => setGroupByCustomer(e.target.checked)}
                  />
                  Group by customer
                </label>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              <LegendItem label="Best vs. baselines" color="#16a34a" />
              <LegendItem label="Behind a baseline" color="#dc2626" />
              <span className="text-xs text-gray-400">
                {isSeasonal ? 'Compared to LTA' : 'Compared to persistence & alt forecasts'}
              </span>
              <span className="text-xs text-gray-500">
                {sites.length
                  ? filterOrg
                    ? `${sites.length} of ${sitesRaw.length} sites`
                    : `${sites.length} sites`
                  : effectiveOverview
                    ? '0 sites'
                    : 'Loading sites…'}
                {effectiveOverview?.days ? `, last ${effectiveOverview.days} days` : ''}
                {isFetching && isLoaded ? ' (refreshing…)' : ''}
              </span>
            </div>
          </div>

          {/* Site list */}
          {!effectiveOverview && !isLoaded ? (
            <ul className="space-y-2">
              {Array.from({length: 10}).map((_, i) => (
                <li key={i} className="rounded-md border border-gray-200 bg-white p-3">
                  <div className="flex justify-between items-center gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="h-4 w-56 bg-gray-100 rounded" />
                      <div className="mt-2 h-3 w-40 bg-gray-100 rounded" />
                    </div>
                    <div className="flex-shrink-0">
                      <div className="h-[30px] w-[210px] bg-gray-50 rounded" />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="space-y-2">
              {sections.map((section) => (
                <React.Fragment key={section.key}>
                  {section.title && (
                    <li className="pt-2">
                      <div className="text-sm font-medium text-gray-900">{section.title}</div>
                      <div className="mt-0.5 text-xs text-gray-500">
                        {section.sites.length} sites
                      </div>
                    </li>
                  )}
                  {section.sites.map((s) => (
                    <li
                      key={s.siteId}
                      data-site-id={s.siteId}
                      onMouseEnter={() => setHoveredSiteId(s.siteId)}
                      onMouseLeave={() => setHoveredSiteId(null)}
                    >
                      {s.skipReason ? (
                        <div className="block rounded-md border border-gray-200 bg-gray-50 p-3 opacity-70">
                          <div className="flex justify-between items-center gap-4">
                            <div className="min-w-0">
                              <SiteName site={s} muted />
                              <div className="mt-1 text-xs text-amber-700">
                                Skipped: {s.skipReason}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <Link
                          to={getSiteDetailUrl(s, product)}
                          className="block rounded-md border border-gray-200 bg-white p-3 hover:border-blue-300 hover:bg-blue-50/30 transition-colors"
                        >
                          <div className="flex justify-between items-center gap-4">
                            <div className="min-w-0">
                              <SiteName site={s} />
                              <div className="mt-1 text-xs text-gray-500 flex items-center gap-2">
                                <MetricBadges site={s} product={product} />
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              {isSeasonal ? (
                                <SeasonalIndicatorsMini data={s.seasonalIndicators} />
                              ) : (
                                <ForecastSparkline data={s.sparkline} />
                              )}
                            </div>
                          </div>
                        </Link>
                      )}
                    </li>
                  ))}
                </React.Fragment>
              ))}
              {sites.length === 0 && (
                <li className="text-sm text-gray-600">No evaluation sites.</li>
              )}
            </ul>
          )}
        </div>
      </div>
    </PageContainer>
  );
}

function LegendItem({label, color}: {label: string; color: string}) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="h-3 w-3 rounded-full" style={{background: color}} />
      {label}
    </span>
  );
}

function SiteName({site, muted}: {site: EvalOverviewSite; muted?: boolean}) {
  return (
    <div
      className={`truncate font-medium flex items-center gap-2 ${muted ? 'text-gray-500' : 'text-gray-900'}`}
    >
      <span className="truncate">{site.displayName || site.siteId}</span>
      {site.displayName && site.displayName !== site.siteId && (
        <span className="text-xs text-gray-400 font-normal truncate">{site.siteId}</span>
      )}
    </div>
  );
}

function MetricBadges({site, product}: {site: EvalOverviewSite; product: EvalProduct}) {
  const hf = site.models?.hydroforecast;
  if (!hf) {
    return <span className="text-gray-400">No evaluations in window</span>;
  }

  const metrics: Array<{
    key: string;
    label: string;
    value: number | undefined;
    format: (v: number) => string;
  }> = [
    {key: 'kge', label: 'KGE', value: hf.kge, format: (v) => v.toFixed(2)},
    {key: 'nrmse', label: 'nRMSE', value: hf.nrmse, format: (v) => v.toFixed(2)},
    {key: 'crps', label: 'CRPS', value: hf.crps, format: (v) => v.toFixed(2)},
    {key: 'mae', label: 'MAE', value: hf.mae, format: (v) => v.toFixed(2)},
    {key: 'percentBias', label: '%Bias', value: hf.percentBias, format: (v) => `${v.toFixed(1)}%`},
  ];

  const available = metrics.filter(
    (m) => m.value !== null && m.value !== undefined && Number.isFinite(m.value)
  );

  if (available.length === 0) return <span className="text-gray-400">No metrics</span>;

  return (
    <span className="inline-flex gap-1 flex-wrap">
      {available.map((m) => (
        <MetricPill
          key={m.key}
          label={m.label}
          value={m.value!}
          metric={m.key}
          format={m.format}
          site={site}
          product={product}
        />
      ))}
    </span>
  );
}

function MetricPill({
  label,
  value,
  metric,
  format,
  site,
  product,
}: {
  label: string;
  value: number;
  metric: string;
  format: (v: number) => string;
  site: EvalOverviewSite;
  product: EvalProduct;
}) {
  const status = metricStatus(metric, value, site.models, product);
  const cls = status ? STATUS_CLASSES[status] : 'bg-gray-100 text-gray-700';
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${cls}`}>
      {label} {format(value)}
    </span>
  );
}
